import React, { ReactNode, useRef, useState } from "react";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Container, Divider, Popover, SxProps, TextareaAutosize, Typography } from "@mui/material";
import { useActionHook } from "hooks";
import { useUserActions } from "store";
import { IRecommendationType } from "store/types/user/social";

import "./MessagePopper.styles.scss";

const style: SxProps = {
  padding: "30px 30px",
  display: "flex",
  flexDirection: "column",
  gap: "15px"
};

const headerStyles: SxProps = {
  gap: "12px",
  color: "#0a3142",
  padding: "0",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end"
};

export interface IMessageModalProps {
  title: string;
  subTitle: string;
  btnTitle: string;
  type: IRecommendationType;
  userId?: number;
  children?: ReactNode;
  placeholder?: string;
}

export const MessagePopper: React.FC<IMessageModalProps> = ({
  title,
  subTitle,
  btnTitle,
  type,
  userId,
  children,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const { createRecommendationMessageAction } = useUserActions();

  const onClose = () => {
    setIsOpen(false);
    setInputValue("");
  };

  const { action } = useActionHook(createRecommendationMessageAction, {
    onSuccess: () => {
      type === "connect" && toast("An email with your request was sent to Altius");
      onClose();
    }
  });

  const containerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {children ? (
        <Box ref={containerRef} onClick={() => setIsOpen(true)}>
          {children}
        </Box>
      ) : (
        <Button
          ref={containerRef}
          onClick={() => setIsOpen(true)}
          sx={{ color: "#0A3142", borderColor: "#0A3142" }}
          variant="outlined"
        >
          {btnTitle}
        </Button>
      )}
      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={containerRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        sx={{
          ".MuiPopover-paper": {
            backgroundColor: "#ffffff",
            minWidth: "480px",
            minHeight: "300px",
            borderRadius: "8px",
            boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
            gap: "10px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center"
          }
        }}
      >
        <Container sx={{ ...style }} maxWidth={false} disableGutters>
          <Box sx={headerStyles}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography fontSize={16} lineHeight={1.5} fontWeight="500" sx={{ marginBottom: "5px" }}>
                  {title}
                </Typography>
                <Typography sx={{ maxWidth: "360px" }} fontSize={14} lineHeight={1.5} color="#5d7782">
                  {subTitle}
                </Typography>
              </Box>
              <CloseIcon sx={{ cursor: "pointer" }} onClick={onClose} />
            </Box>
            <Divider
              sx={{
                height: "1px",
                width: "100%",
                borderBottomWidth: "1px",
                backgroundColor: "#dae0e2"
              }}
            />
          </Box>
          <Box>
            <TextareaAutosize
              className="message-popper"
              placeholder={placeholder || "Message"}
              value={inputValue}
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
            />
          </Box>
          <Box display="flex" gap="15px" alignItems="center" justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!inputValue.trim()}
              onClick={() => action({ message: inputValue, type, user_id: userId })}
              variant="contained"
            >
              Send
            </Button>
          </Box>
        </Container>
      </Popover>
    </>
  );
};
